import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical, AlertTriangle, Handshake, Loader2, ExternalLink, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATS = [["broker", "Brokers"], ["prop_firm", "Prop Firms"], ["software", "Software"]];
// Full class strings so Tailwind keeps them in the build.
export const ACCENTS = [
  "from-blue-700 to-indigo-800", "from-sky-600 to-indigo-700", "from-sky-500 to-blue-700", "from-cyan-500 to-blue-700",
  "from-emerald-500 to-emerald-700", "from-emerald-600 to-teal-700", "from-lime-500 to-emerald-700",
  "from-amber-500 to-orange-600", "from-orange-500 to-red-600", "from-rose-500 to-red-700", "from-rose-600 to-red-700",
  "from-violet-600 to-purple-800", "from-violet-600 to-fuchsia-700", "from-slate-700 to-slate-900", "from-slate-800 to-slate-950",
  "from-blue-600 to-blue-800",
];
const EMPTY_TEXT = { tagline: "", blurb: "", perk: "" };
const blank = (category) => ({ category, name: "", url: "", domain: "", logo_url: "", code: "", code_verified: false,
  accent: ACCENTS[0], initial: "", active: true, text: { en: { ...EMPTY_TEXT }, es: { ...EMPTY_TEXT } } });

function TextFields({ lang, value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{lang === "en" ? "English" : "Spanish (auto-filled when blank)"}</div>
      <Input placeholder="Tagline (e.g. Futures prop firm · Same-day payouts)" value={value.tagline} onChange={set("tagline")} data-testid={`partner-form-${lang}-tagline`} />
      <Textarea rows={3} placeholder="Short description" value={value.blurb} onChange={set("blurb")} data-testid={`partner-form-${lang}-blurb`} />
      <Input placeholder="Perk (e.g. 10% off your first challenge)" value={value.perk} onChange={set("perk")} data-testid={`partner-form-${lang}-perk`} />
    </div>
  );
}

function PartnerForm({ form, setForm, onSave, saving }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-xs font-semibold text-slate-600">Name</span>
          <Input value={form.name} onChange={set("name")} data-testid="partner-form-name" /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Category</span>
          <select value={form.category} onChange={set("category")} data-testid="partner-form-category" className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
            {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
      </div>
      <label className="block"><span className="text-xs font-semibold text-slate-600">Website / referral link</span>
        <Input value={form.url} onChange={set("url")} placeholder="https://…" data-testid="partner-form-url" /></label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-xs font-semibold text-slate-600">Promo code (optional)</span>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value, code_verified: e.target.value ? form.code_verified : false })} data-testid="partner-form-code" /></label>
        <label className="flex items-center gap-2 pt-6 text-sm text-slate-700">
          <Switch checked={form.code_verified} disabled={!form.code} onCheckedChange={(v) => setForm({ ...form, code_verified: v })} data-testid="partner-form-code-verified" />
          Code is real; show it to members
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block sm:col-span-2"><span className="text-xs font-semibold text-slate-600">Logo URL (optional; uses the site icon if blank)</span>
          <Input value={form.logo_url} onChange={set("logo_url")} data-testid="partner-form-logo" /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Initials</span>
          <Input value={form.initial} maxLength={3} onChange={set("initial")} data-testid="partner-form-initial" /></label>
      </div>
      <div>
        <span className="text-xs font-semibold text-slate-600">Badge color</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {ACCENTS.map((a) => (
            <button key={a} type="button" onClick={() => setForm({ ...form, accent: a })} aria-label={a}
              className={`w-7 h-7 rounded-lg bg-gradient-to-br ${a} ${form.accent === a ? "ring-2 ring-offset-2 ring-blue-600" : ""}`} />
          ))}
        </div>
      </div>
      <TextFields lang="en" value={form.text.en} onChange={(v) => setForm({ ...form, text: { ...form.text, en: v } })} />
      <TextFields lang="es" value={form.text.es} onChange={(v) => setForm({ ...form, text: { ...form.text, es: v } })} />
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="partner-form-active" /> Visible to members
        </label>
        <div className="flex gap-2">
          {form.id && <Button variant="outline" disabled={saving} onClick={() => onSave(true)} data-testid="partner-form-retranslate">Re-translate Spanish</Button>}
          <Button disabled={saving || !form.name || !form.url} onClick={() => onSave(false)} className="bg-blue-900 hover:bg-blue-800" data-testid="partner-form-save">
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function PartnerRow({ p, onEdit, onDelete, drag }) {
  const logo = p.logo_url || (p.domain ? `https://www.google.com/s2/favicons?domain=${p.domain}&sz=64` : "");
  return (
    <div draggable {...drag} data-testid={`admin-partner-row-${p.id}`}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 hover:border-slate-300">
      <GripVertical size={16} className="text-slate-300 cursor-grab shrink-0" />
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${p.accent} p-1.5 shrink-0`}>
        {logo && <img src={logo} alt="" className="w-full h-full object-contain rounded bg-white/90" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-900">{p.name}</span>
          {!p.active && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-slate-500"><EyeOff size={10} /> hidden</span>}
          {p.code && !p.code_verified && (
            <span data-testid={`admin-partner-needs-code-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
              <AlertTriangle size={10} /> Needs real code
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {p.code ? <span className="font-mono">{p.code}</span> : "No code"} · {p.clicks} clicks ·{" "}
          <a href={p.url} target="_blank" rel="noreferrer" className="hover:text-blue-800 inline-flex items-center gap-0.5">{p.url} <ExternalLink size={10} /></a>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onEdit(p)} data-testid={`admin-partner-edit-${p.id}`}><Pencil size={14} /></Button>
      <Button variant="ghost" size="sm" onClick={() => onDelete(p)} className="text-red-600" data-testid={`admin-partner-delete-${p.id}`}><Trash2 size={14} /></Button>
    </div>
  );
}

export default function AdminPartners() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("prop_firm");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => api.get("/admin/partners").then((r) => setItems(r.data)).catch(() => toast.error("Couldn't load partners")), []);
  useEffect(() => { load(); }, [load]);
  const list = useMemo(() => items.filter((p) => p.category === cat), [items, cat]);

  const save = async (retranslate) => {
    setSaving(true);
    const { id, text, clicks, order, slug, ...rest } = form;
    const payload = { ...rest, en: text.en, es: text.es, retranslate_es: retranslate };
    try {
      const { data } = id ? await api.put(`/admin/partners/${id}`, payload) : await api.post("/admin/partners", payload);
      toast.success(`${data.name} saved`);
      if (retranslate) setForm(data); else setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success(`${p.name} deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
    load();
  };

  const dropOn = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = list.map((p) => p.id).filter((x) => x !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    setItems((prev) => [...prev.filter((p) => p.category !== cat), ...ids.map((x) => prev.find((p) => p.id === x))]);
    setDragId(null);
    await api.post("/admin/partners/reorder", { category: cat, ids }).catch(() => toast.error("Reorder failed"));
  };

  const needsCode = items.filter((p) => p.code && !p.code_verified).length;
  return (
    <div data-testid="admin-partners-root" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Handshake size={26} className="text-amber-600" /> Partners</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">Brokers, prop firms and software shown in The Vault. Drag to reorder. Codes flagged "Needs real code" stay hidden from members until you mark them real.</p>
        </div>
        <Button onClick={() => setForm(blank(cat))} className="bg-blue-900 hover:bg-blue-800" data-testid="admin-partner-add"><Plus size={14} className="mr-1.5" /> Add partner</Button>
      </header>
      {needsCode > 0 && (
        <div data-testid="admin-partners-needs-code-banner" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={14} /> {needsCode} partner code{needsCode === 1 ? "" : "s"} still need your real referral code.
        </div>
      )}
      <div className="flex gap-2">
        {CATS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setCat(v)} data-testid={`admin-partners-tab-${v}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${cat === v ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-300 text-slate-700"}`}>
            {l} <span className="opacity-60">({items.filter((p) => p.category === v).length})</span>
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {list.map((p) => (
          <PartnerRow key={p.id} p={p} onEdit={(x) => setForm(x)} onDelete={remove}
            drag={{ onDragStart: () => setDragId(p.id), onDragOver: (e) => e.preventDefault(), onDrop: () => dropOn(p.id) }} />
        ))}
        {!list.length && <p className="text-sm text-slate-500">No partners in this list yet.</p>}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-partner-dialog">
          <DialogHeader><DialogTitle>{form?.id ? `Edit ${form.name}` : "Add partner"}</DialogTitle></DialogHeader>
          {form && <PartnerForm form={form} setForm={setForm} onSave={save} saving={saving} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
